// @flow
import React, { Children, type Node } from 'react';
import classnames from 'classnames';

import { COLORS } from 'Components/utils/color/colors';

import { ArrowButton } from 'Components/frameworks/arrow-button/ArrowButton.jsx';
import { ProgressBar } from 'Components/frameworks/progress-bar/ProgressBar.jsx';
import { SliderHandler } from 'Components/frameworks/slider-handler/SliderHandler.jsx';

type Props = {
  children: Node,
  className?: string,
  hasInfiniteScroll?: boolean,
  initialIndex?: number,
  nextLabel?: string,
  previousLabel?: string,
};

export function Carousel(props: Props) {
  const {
    children,
    className,
    hasInfiniteScroll,
    initialIndex = 0,
    nextLabel,
    previousLabel,
  } = props;

  const childrenCount = Children.toArray(children).filter(Boolean).length;

  if (!childrenCount) return null;

  return (
    <div className={classnames(className, 'overflow-hidden relative')}>
      <SliderHandler
        className="h-full w-full"
        hasInfiniteScroll={hasInfiniteScroll}
        hasKeyboardSupport
        hasSwipeSupport
        initialIndex={initialIndex}
        items={children}
      >
        {({
          hasControls,
          index,
          isNextControlDisabled,
          isPreviousControlDisabled,
          itemsNode,
          onNext,
          onPrevious,
        }) => (
          <div className="flex-1 flex pb-5 relative">
            {itemsNode}
            {hasControls && (
              <div className="absolute flex items-center justify-between m-auto inset-0 pointer-events-none px-5 w-full">
                {!isPreviousControlDisabled && (
                  <ArrowButton
                    className="rtl:mr-auto pointer-events-auto shadow-sm z-1"
                    label={previousLabel}
                    onClick={onPrevious}
                  />
                )}
                {!isNextControlDisabled && (
                  <ArrowButton
                    className="rtl:ml-auto pointer-events-auto shadow-sm z-1"
                    label={nextLabel}
                    onClick={onNext}
                    rotation={180}
                  />
                )}
              </div>
            )}
            {hasControls && (
              <div className="absolute bottom-0 w-full">
                <ProgressBar color={COLORS.WHITE} max={childrenCount} min={1} value={index + 1} />
              </div>
            )}
          </div>
        )}
      </SliderHandler>
    </div>
  );
}
